import { legalSyllablesAt } from './syllables';

/**
 * One surname, spelled every way a family has ever spelled it.
 *
 * The roster is almost entirely mainland Hanyu Pinyin — 185 of 267 active children carry a
 * Han surname and it is written zhang, liu, chen, li, yang. But the name arrives by ear from
 * a parent at the gate, and what the recogniser writes down is whatever English spelling
 * fits the sound: Chang, Cheung, Teoh, all of them 张. Wade-Giles, Cantonese, Hokkien,
 * Singaporean and plain English-phonetic spellings all land in the same transcript field.
 *
 * So this maps *outward* spellings back to the pinyin the roster uses. The reverse direction
 * is generated rather than written twice, because a table maintained in two directions drifts
 * in one of them.
 *
 * Extending this is meant to be trivial: add a line. That is the whole design. It is a data
 * file precisely so that the next person to find a name this misses does not have to
 * understand the matcher to fix it.
 */

/** canonical pinyin → the spellings a recogniser might produce for it. */
const VARIANTS: Record<string, readonly string[]> = {
  // The ticket's starting table.
  chen: ['chan', 'chin', 'tan', 'chun', 'ting'],
  zhang: ['chang', 'cheung', 'teoh', 'teo', 'jang', 'chong', 'tiong'],
  huang: ['hwang', 'wong', 'ng', 'oey', 'uy'],
  liu: ['lau', 'low', 'liou', 'lew', 'lieu', 'liew'],
  xie: ['hsieh', 'tse', 'cheah', 'seah', 'shieh'],
  zhou: ['chow', 'chau', 'jou', 'chou', 'jow'],
  xu: ['hsu', 'tsui', 'hui', 'shu', 'chee', 'koh'],
  cai: ['tsai', 'choi', 'choy', 'chua', 'chai', 'tsay'],
  guo: ['kuo', 'kwok', 'quek', 'kwo', 'gwok'],
  li: ['lee', 'ly', 'lie', 'lei', 'ley'],
  wang: ['wong', 'ong', 'vong', 'waung'],
  wu: ['ng', 'woo', 'goh', 'ngo', 'oo'],
  lin: ['lam', 'lim', 'lym'],
  ye: ['yip', 'yeh', 'ip', 'yap'],
  feng: ['fung', 'foong', 'fong'],

  // Added from this roster's own surname distribution — every surname held by three or more
  // active children, plus the ones whose English spelling is furthest from the pinyin.
  yang: ['yeung', 'yong', 'yiong', 'young'],
  zhao: ['chao', 'chiu', 'jao', 'chew'],
  zhu: ['chu', 'chue', 'gee', 'jue'],
  yu: ['yee', 'yue', 'yu', 'eu', 'yew'],
  he: ['ho', 'hor', 'hoh'],
  jiang: ['chiang', 'kong', 'keong', 'geong'],
  shi: ['shih', 'sze', 'seah', 'shee'],
  ge: ['ko', 'kot', 'gor'],
  cao: ['tsao', 'cho', 'chow'],
  sun: ['suen', 'soon', 'shuen'],
  ma: ['mah', 'mar'],
  hu: ['woo', 'foo', 'hoo'],
  gao: ['kao', 'ko', 'kou'],
  luo: ['lo', 'law', 'loh', 'lowe'],
  zheng: ['cheng', 'chang', 'tay', 'te'],
  liang: ['leung', 'leong', 'niu'],
  song: ['sung', 'soong'],
  tang: ['tong', 'thong'],
  deng: ['teng', 'tang'],
  peng: ['pang', 'phang'],
  zeng: ['tseng', 'tsang', 'chan', 'chen'],
  xue: ['hsueh', 'sit', 'shirt'],
  lu: ['loo', 'lu', 'look', 'luk'],
  shen: ['shum', 'sum', 'sam'],
  han: ['hon', 'hahn'],
  pan: ['poon', 'phua', 'pun'],
  du: ['to', 'toh', 'doo'],
  fan: ['fahn', 'huan', 'faan'],
  fang: ['fong', 'pong'],
  yao: ['yew', 'yao', 'iao'],
  tan: ['tam', 'tahm'],
  qiu: ['chiu', 'yau', 'khoo', 'chew'],
  qin: ['chin', 'ching'],
  wei: ['wai', 'ngai', 'way'],
  yan: ['yen', 'ngan', 'ien'],
  ren: ['yam', 'jen', 'yum'],
  su: ['soo', 'sou', 'saw'],
  gu: ['koo', 'ku', 'goo'],
  kong: ['hong', 'khong'],
  cui: ['tsui', 'choi', 'chwee'],
  zhong: ['chung', 'chong', 'jong'],
  ding: ['ting', 'teng'],
  jin: ['kim', 'chin', 'king'],
  fu: ['foo', 'poh', 'hu'],
  xiao: ['siew', 'shaw', 'hsiao', 'siu', 'shiu'],
  cheng: ['ching', 'chen', 'zheng'],
  dai: ['tai', 'day', 'toy'],
  bai: ['pai', 'baak', 'bak'],
  tian: ['tien', 'thien'],
  meng: ['mang', 'mong'],
  // 'tsien' is how 錢 is spelled outside Wade-Giles proper (Tsien Hsue-shen), and this
  // roster uses it inside a *given* name — Tsien- glued to a second syllable — which is
  // why the lookup below had to stop being whole-token. The table was written for surnames
  // but the mapping is per-syllable and always was; nothing here is surname-specific
  // except the comment that used to say so.
  qian: ['chien', 'chin', 'chee', 'tsien'],
  zou: ['chow', 'chau', 'jou'],
  hou: ['hau', 'how', 'hoe'],
  duan: ['tuan', 'tun'],
  lei: ['loi', 'louie', 'lui'],
  jia: ['chia', 'ka', 'gaa'],
  lai: ['loi', 'lye', 'lei'],
  mo: ['mok', 'mock', 'mor'],
  an: ['on', 'ahn'],
  ke: ['ko', 'kor', 'quah'],
  // Added from a coverage audit against the live roster: every Han surname a child is
  // actually enrolled under that the table could not read. Two kinds are in here and they
  // fail differently. Most are ordinary pinyin the table simply had no row for, so a
  // dialect spelling in a transcript found nothing. A handful — lyu, nieh, tung, auyeung —
  // are the *roster* spelled in something other than pinyin, which is invisible to every
  // layer until the table can rewrite the roster's own row.
  gui: ['kwai', 'kwei', 'gway', 'kwee', 'gwai', 'kuei'],
  lv: ['lyu', 'lu', 'lui', 'leui'],
  nie: ['nieh', 'nip'],
  dong: ['tung', 'tong', 'dung'],
  ouyang: ['auyeung', 'owyang', 'auyeong'],
  guan: ['kwan', 'kuan', 'koon'],
  kuang: ['kwong', 'kong', 'hong'],
  weng: ['yung', 'ung', 'yong'],
  zhai: ['chai', 'jai', 'dik'],
  sheng: ['seng', 'shing', 'sing'],
  shang: ['sheung', 'seung'],
  shan: ['sin', 'sim'],
  lian: ['lien', 'lin', 'lim'],
  jian: ['kan', 'gan', 'kin'],
  yin: ['wan', 'yan', 'ngan'],
  miao: ['miu', 'mew'],
  liao: ['leow', 'liaw', 'liew'],
  tu: ['too', 'tou', 'doo'],
  mu: ['moo', 'mook'],
  ling: ['leng', 'ling'],
  kuai: ['kwai', 'fai'],
  zang: ['tsang', 'chong'],
  ran: ['yim'],
  niu: ['new', 'nau', 'gnau'],
};

/** variant spelling → the canonical pinyin syllables it could be. */
const TO_CANONICAL = new Map<string, string[]>();
for (const [canonical, variants] of Object.entries(VARIANTS)) {
  for (const variant of [canonical, ...variants]) {
    const existing = TO_CANONICAL.get(variant);
    if (existing) {
      if (!existing.includes(canonical)) existing.push(canonical);
    } else {
      TO_CANONICAL.set(variant, [canonical]);
    }
  }
}

/**
 * The canonical pinyin spellings `token` could be, including itself.
 *
 * More than one is normal and not a defect: "ng" is both 黄 and 吴, "chow" is 周 and 曹,
 * "chan" is 陈 and 曾. All of them are returned and the scorer takes whichever matches the
 * roster — which is the right resolution, because the roster is the thing that knows which
 * families actually attend this school.
 */
export function canonicalSpellings(token: string): string[] {
  const t = token.toLowerCase();
  const mapped = TO_CANONICAL.get(t);
  if (!mapped) return [t];
  return mapped.includes(t) ? mapped : [t, ...mapped];
}

/** Every variant spelling, longest first, so a prefix walk takes the longest match. */
const VARIANT_KEYS = [...TO_CANONICAL.keys()].sort((a, b) => b.length - a.length);

const FORM_CACHE = new Map<string, string[]>();
const FORM_CACHE_LIMIT = 5_000;

/** Nodes one walk may visit. A cap, not a tuning knob: real names finish far inside it. */
const WALK_BUDGET = 2_000;

/**
 * The token rewritten into pinyin, where part of it — not necessarily all of it — was
 * spelled some other way.
 *
 * `canonicalSpellings` looks the whole token up in the table, which works for a surname
 * standing alone ("Cheung") and cannot work for anything glued together. A Chinese given
 * name is routinely written as one word, and when a family spells it the way they spell
 * their surname the result is a token like "Tsienhua": one variant syllable ("tsien" → qian)
 * followed by one plain pinyin syllable ("mo"). Nothing in the table matches that string, so
 * before this the roster's own name was invisible to every layer — `tsienhua` has no legal
 * segmentation, because `ts-` is not a pinyin initial.
 *
 * So the token is walked in syllables, taking either a table variant or a legal pinyin
 * syllable at each step, and the readings that used the table at least once are returned.
 * Forms identical to the input are dropped: every caller already tries the token as written.
 */
export function canonicalTokenForms(token: string, limit = 8): string[] {
  const t = token.toLowerCase().replace(/[^a-z]/g, '');
  if (t === '') return [];
  const cached = FORM_CACHE.get(t);
  if (cached) return cached;

  const out: string[] = [];
  const seen = new Set<string>();
  let budget = WALK_BUDGET;

  const walk = (at: number, acc: string, rewrote: boolean): void => {
    if (out.length >= limit || budget-- <= 0) return;
    if (at === t.length) {
      if (rewrote && acc !== t && !seen.has(acc)) {
        seen.add(acc);
        out.push(acc);
      }
      return;
    }
    for (const key of VARIANT_KEYS) {
      if (!t.startsWith(key, at)) continue;
      // A Mandarin syllable always has a vowel. A variant spelling without one is a whole
      // name written that way — Cantonese "Ng" for 黄/吴 — and never a syllable inside a
      // longer name. Allowing it mid-token read the -ng coda of every ordinary syllable as
      // 黄: "tang" came back as "tahuang", and the noise outscored real names.
      if (!/[aeiouv]/.test(key) && key.length !== t.length) continue;
      for (const canonical of TO_CANONICAL.get(key) ?? []) {
        walk(at + key.length, acc + canonical, rewrote || canonical !== key);
      }
    }
    for (const syllable of legalSyllablesAt(t, at)) {
      walk(at + syllable.length, acc + syllable, rewrote);
    }
  };
  walk(0, '', false);

  if (FORM_CACHE.size >= FORM_CACHE_LIMIT) FORM_CACHE.clear();
  FORM_CACHE.set(t, out);
  return out;
}

/** True when the two spellings are known variants of one surname. */
export function sameRomanizedName(a: string, b: string): boolean {
  const ca = new Set(canonicalSpellings(a));
  return canonicalSpellings(b).some(c => ca.has(c));
}

/** Every spelling in the table, for the eval harness's coverage report. */
export function tableSize(): { canonical: number; variants: number } {
  return { canonical: Object.keys(VARIANTS).length, variants: TO_CANONICAL.size };
}
